import { BACKEND_URL, AUTH_REFRESH_MARGIN_MS, AUTH_EMBED_MODE_KEY } from "@/config/constants";

export interface TokenData {
  dataToken: string;
  expiresAt: number;
  user: Record<string, unknown>;
  viewPermissions: Array<{ viewId: string; actions: string[] }>;
  appId?: string;
  deploymentId?: string;
  showNavigation?: boolean;
  showHeaderNavigation?: boolean;
  showSidebarNavigation?: boolean;
  refreshToken?: string;
}

let sessionData: TokenData | null = null;
let refreshTimerId: number | null = null;
let refreshLogIntervalId: number | null = null;

export function decodeJwt<T = Record<string, unknown>>(token: string): T {
  const payload = token.split(".")[1];
  return JSON.parse(atob(payload));
}

export function isTokenExpiringSoon(expiresAt: number): boolean {
  return expiresAt - Date.now() < AUTH_REFRESH_MARGIN_MS;
}

export function isTokenExpired(expiresAt: number): boolean {
  return expiresAt < Date.now();
}

function setSessionData(data: TokenData | null): void {
  sessionData = data;
}

export function getStoredTokens(): TokenData | null {
  return sessionData;
}

export function setSessionFromRefreshResponse(data: {
  dataToken: string;
  expiresAt: number;
  user?: Record<string, unknown>;
  viewPermissions?: Array<{ viewId: string; actions: string[] }>;
  appId?: string;
  deploymentId?: string;
  showNavigation?: boolean;
  showHeaderNavigation?: boolean;
  showSidebarNavigation?: boolean;
}): void {
  const payload: TokenData = {
    dataToken: data.dataToken,
    expiresAt: data.expiresAt,
    user: data.user ?? {},
    viewPermissions: data.viewPermissions ?? [],
    appId: data.appId,
    deploymentId: data.deploymentId,
    showNavigation: data.showNavigation,
    showHeaderNavigation: data.showHeaderNavigation,
    showSidebarNavigation: data.showSidebarNavigation,
  };
  setSessionData(payload);
  setupRefreshTimer(payload.expiresAt);
}

function clearRefreshTimer(): void {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  if (refreshLogIntervalId) {
    clearInterval(refreshLogIntervalId);
    refreshLogIntervalId = null;
  }
}

function getPostMessageService(): { send: (m: unknown) => void } | null {
  try {
    const PostMessageService = (window as unknown as { PostMessageService?: { getInstance: () => { send: (m: unknown) => void } } })
      .PostMessageService;
    if (PostMessageService) return PostMessageService.getInstance();
  } catch {
    // ignore
  }
  return null;
}

function notifyParentAuthExpired(): void {
  const service = getPostMessageService();
  if (service) {
    service.send({
      type: "AUTH_EXPIRED",
      message: "Session expired, re-authentication required",
      timestamp: Date.now(),
    });
    console.log("[auth] AUTH_EXPIRED sent to parent");
  }
}

function notifyParentSessionExpiringSoon(expiresAt: number): void {
  const service = getPostMessageService();
  if (service) {
    service.send({
      type: "SESSION_EXPIRING_SOON",
      expiresAt,
      timestamp: Date.now(),
    });
    console.log("[auth] SESSION_EXPIRING_SOON sent to parent");
  }
}

async function doRefresh(): Promise<boolean> {
  console.log("[auth] Refreshing now...");
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      console.log("[auth] Refresh failed:", response.status, await response.text().catch(() => ""));
      return false;
    }

    const data = await response.json();
    setSessionData({
      dataToken: data.dataToken,
      expiresAt: data.expiresAt,
      user: data.user ?? {},
      viewPermissions: data.viewPermissions ?? [],
      appId: data.appId,
      deploymentId: data.deploymentId,
      showNavigation: data.showNavigation,
      showHeaderNavigation: data.showHeaderNavigation,
      showSidebarNavigation: data.showSidebarNavigation,
    });
    setupRefreshTimer(data.expiresAt);
    return true;
  } catch {
    return false;
  }
}

export function setupRefreshTimer(expiresAt: number): void {
  clearRefreshTimer();
  const now = Date.now();
  const timeUntilExpiry = expiresAt - now;
  const timeUntilRefresh = timeUntilExpiry - AUTH_REFRESH_MARGIN_MS;

  if (timeUntilRefresh <= 0) {
    if (timeUntilExpiry > 0) {
      if (getEmbedModeFlag()) {
        notifyParentSessionExpiringSoon(expiresAt);
      } else {
        doRefresh().then((success) => {
          if (!success) {
            setSessionData(null);
            clearRefreshTimer();
            notifyParentAuthExpired();
          }
        });
      }
    } else {
      setSessionData(null);
      notifyParentAuthExpired();
    }
  } else {
    const refreshAt = Date.now() + timeUntilRefresh;
    const formatCountdown = (ms: number) => {
      const s = Math.round(ms / 1000);
      if (s >= 60) return `${Math.floor(s / 60)} min ${s % 60} s`;
      return `${s} s`;
    };
    console.log("[auth] Refresh scheduled in", formatCountdown(timeUntilRefresh));
    refreshLogIntervalId = window.setInterval(() => {
      const remaining = Math.max(0, refreshAt - Date.now());
      if (remaining <= 0) return;
      console.log("[auth] Refresh in ~", formatCountdown(remaining));
    }, 15000);
    refreshTimerId = window.setTimeout(() => {
      if (getEmbedModeFlag()) {
        notifyParentSessionExpiringSoon(expiresAt);
      } else {
        doRefresh().then((success) => {
          if (!success) {
            setSessionData(null);
            clearRefreshTimer();
            notifyParentAuthExpired();
          }
        });
      }
    }, timeUntilRefresh);
  }
}

export function initSession(data?: TokenData): boolean {
  setEmbedMode(true);
  if (data?.user && data?.expiresAt) {
    setSessionData(data);
    setupRefreshTimer(data.expiresAt);
  }
  return true;
}

export function applyRefreshResult(data: {
  dataToken: string;
  user: Record<string, unknown>;
  viewPermissions?: Array<{ viewId: string; actions: string[] }>;
  expiresAt: number;
  appId?: string;
  deploymentId?: string;
  showNavigation?: boolean;
  showHeaderNavigation?: boolean;
  showSidebarNavigation?: boolean;
}): void {
  setSessionData({
    dataToken: data.dataToken,
    expiresAt: data.expiresAt,
    user: data.user ?? {},
    viewPermissions: data.viewPermissions ?? [],
    appId: data.appId,
    deploymentId: data.deploymentId,
    showNavigation: data.showNavigation,
    showHeaderNavigation: data.showHeaderNavigation,
    showSidebarNavigation: data.showSidebarNavigation,
  });
  setupRefreshTimer(data.expiresAt);
  console.log("[auth] Session updated from REFRESH_RESULT");
}

export async function recoverSession(): Promise<TokenData | null> {
  return null;
}

export async function clearTokens(): Promise<void> {
  try {
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // ignore
  }
  setSessionData(null);
  clearRefreshTimer();
  localStorage.removeItem(AUTH_EMBED_MODE_KEY);
}

export function getCurrentUser(): Record<string, unknown> | null {
  return sessionData?.user ?? null;
}

export function getViewPermissions(): Array<{ viewId: string; actions: string[] }> {
  return sessionData?.viewPermissions ?? [];
}

export function hasPermission(viewId: string, action: "READ" | "EDIT" | "ADD" | "DELETE"): boolean {
  const permissions = getViewPermissions();
  const viewPerm = permissions.find((p) => p.viewId === viewId);
  return viewPerm?.actions?.includes(action) ?? false;
}

export function setEmbedMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(AUTH_EMBED_MODE_KEY, "true");
  } else {
    localStorage.removeItem(AUTH_EMBED_MODE_KEY);
  }
}

export function getEmbedModeFlag(): boolean {
  return localStorage.getItem(AUTH_EMBED_MODE_KEY) === "true";
}

export function getShowNavigationFlag(): boolean {
  if (!sessionData) return false;
  if (sessionData.showHeaderNavigation ?? false) return true;
  if (sessionData.showSidebarNavigation ?? false) return true;
  return sessionData.showNavigation ?? false;
}

export function getShowHeaderNavigationFlag(): boolean {
  if (!sessionData) return false;
  if (sessionData.showHeaderNavigation !== undefined) return sessionData.showHeaderNavigation;
  return sessionData.showNavigation ?? false;
}

export function getShowSidebarNavigationFlag(): boolean {
  if (!sessionData) return false;
  if (sessionData.showSidebarNavigation !== undefined) return sessionData.showSidebarNavigation;
  return sessionData.showNavigation ?? false;
}
